import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import type { TooltipProps } from 'recharts'
import { useTheme } from '../contexts/ThemeContext'
import type { VehicleFilter } from '../contexts/VehicleFilterContext'
import { publicAPI } from '../services/api'
import { useLatest } from '../pages/Bruktbil/useLatest'
import { useMineMerker } from '../hooks/useMineMerker'
import MerkeMultiSelect from './MerkeMultiSelect'
import ExportButtons from './ExportButtons'
import LoadingSpinner from './LoadingSpinner'
import { ColumnDefinition } from '../utils/exportToCsv'
import { localizeApiDetail } from '../i18n/apiError'
import { snapshotSvg } from '../utils/snapshotSvg'

/**
 * Perioder > Graf (#685): one line per brand per ISO week or month, with the
 * comparison period dashed in the same colour. Buckets are aligned by index —
 * week 1 of the period against week 1 of the comparison period.
 */

interface BrandValue {
  count: number
  share: number
}

interface Bucket {
  index: number
  label: string
  period_start: string
  period_end: string
  is_partial: boolean
  total: number
  brands: Record<string, BrandValue>
}

interface Series {
  start_date: string
  end_date: string
  buckets: Bucket[]
}

interface TimeseriesResponse {
  granularity: 'week' | 'month'
  brands: string[]
  current: Series
  comparison: Series | null
  data_through: string
}

interface Props {
  startDate: string
  endDate: string
  prevStartDate: string
  prevEndDate: string
  vehicleFilter: VehicleFilter
  minePostnummer: string | null
  /** Brands with sales in the current period, largest first */
  brandOptions: string[]
}

const MAX_BRANDS = 5

const BRAND_COLORS = [
  '#e11d48', // rose-600
  '#7c3aed', // violet-600
  '#059669', // emerald-600
  '#d97706', // amber-600
  '#2563eb', // blue-600
]

const fmtDate = (d: string) => {
  const parts = d.split('-')
  return parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : d
}

const fmtValue = (v: number, mode: 'antall' | 'andel') =>
  mode === 'antall'
    ? v.toLocaleString('nb-NO')
    : `${v.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`

const vehicleText = (f: VehicleFilter, t: TFunction<'analytics'>) =>
  t(f === 'personbil' ? 'periodChart.vehiclesPersonbil' : f === 'varebil' ? 'periodChart.vehiclesVarebil' : 'periodChart.vehiclesAll')

export default function BrandPeriodChart({
  startDate,
  endDate,
  prevStartDate,
  prevEndDate,
  vehicleFilter,
  minePostnummer,
  brandOptions,
}: Props) {
  const { t } = useTranslation('analytics')
  const { isDark } = useTheme()
  const [selected, setSelected] = useState<string[]>([])
  const [touched, setTouched] = useState(false)
  const [granularity, setGranularity] = useState<'week' | 'month'>('week')
  const [mode, setMode] = useState<'antall' | 'andel'>('antall')
  const [showComparison, setShowComparison] = useState(true)
  const [data, setData] = useState<TimeseriesResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const chartRef = useRef<HTMLDivElement>(null)
  const chartImage = useRef<string | undefined>(undefined)

  // Default to the user's active Mine merker, or the five largest brands when
  // they have none, until the user picks their own. Mine merker holds SVV
  // names (DEEPAL); the options and the chart use display names (CHANGAN), so
  // map with the backend's name and de-duplicate — one brand, one slot (#782).
  const { items: mineMerkerItems, loading: mineMerkerLoading } = useMineMerker()
  const mineMerker = useMemo(
    () => [...new Set(mineMerkerItems
      .filter(m => m.is_active)
      .sort((a, b) => a.sort_order - b.sort_order)
      .map(m => m.visningsnavn ?? m.merke_navn))],
    [mineMerkerItems]
  )
  const defaultBrands = mineMerker.length ? mineMerker.slice(0, MAX_BRANDS) : brandOptions.slice(0, MAX_BRANDS)
  const defaultKey = mineMerkerLoading ? '' : defaultBrands.join('|')
  useEffect(() => {
    if (!touched && defaultKey) setSelected(defaultKey.split('|'))
  }, [defaultKey, touched])

  const selectedKey = selected.join('|')
  const varebil = vehicleFilter === 'varebil' ? true : vehicleFilter === 'personbil' ? false : undefined

  useLatest(alive => {
    if (!selectedKey || !startDate || !endDate) {
      setData(null)
      return
    }
    setLoading(true)
    setError(null)
    publicAPI
      .getBrandPeriodTimeseries({
        start_date: startDate,
        end_date: endDate,
        prev_start_date: prevStartDate || undefined,
        prev_end_date: prevEndDate || undefined,
        merke: selectedKey.split('|'),
        granularity,
        varebil,
        mine_postnummer: minePostnummer ?? undefined,
      })
      .then(r => { if (alive()) setData(r.data) })
      .catch(err => {
        if (alive()) setError(localizeApiDetail(err.response?.data?.detail) || err.message || t('periodChart.loadFailed'))
      })
      .finally(() => { if (alive()) setLoading(false) })
  }, [startDate, endDate, prevStartDate, prevEndDate, selectedKey, granularity, varebil, minePostnummer])

  const brands = data?.brands ?? []
  const hasComparison = showComparison && !!data?.comparison

  const chartData = useMemo(() => {
    if (!data) return []
    const cur = data.current.buckets
    const prev = data.comparison?.buckets ?? []
    const pick = (v: BrandValue | undefined) => (v ? (mode === 'antall' ? v.count : v.share) : null)
    // The axis is the current period: a longer comparison is cut to it, so the
    // two periods' labels never mix on the x-axis (#861).
    return cur.map((c, i) => {
      const p = prev[i]
      // The backend zero-fills buckets past the data cut-off; drawn as 0 they
      // read as a collapse, so the current line stops there instead (#782).
      const noData = c.period_start > data.data_through
      const row: Record<string, string | number | boolean | null> = {
        index: i,
        label: c.label,
        curLabel: c.label,
        curPartial: c.is_partial,
        prevLabel: p ? p.label : null,
        prevPartial: !!p?.is_partial,
      }
      for (const b of data.brands) {
        row[`c_${b}`] = noData ? null : pick(c.brands[b])
        row[`p_${b}`] = pick(p?.brands[b])
      }
      return row
    })
  }, [data, mode])

  // Keep a PNG of the rendered chart ready for the PDF export.
  useEffect(() => {
    chartImage.current = undefined
    if (!chartData.length) return
    const t = setTimeout(() => snapshotSvg(chartRef.current, png => { chartImage.current = png }), 600)
    return () => clearTimeout(t)
  }, [chartData, hasComparison, isDark])

  const exportRows = useMemo(() => {
    if (!data) return []
    const rows: Record<string, string | number>[] = []
    const add = (periode: string, s: Series) => {
      for (const b of s.buckets) {
        for (const brand of data.brands) {
          const v = b.brands[brand]
          rows.push({
            periode,
            label: b.label,
            fra: b.period_start,
            til: b.period_end,
            merke: brand,
            antall: v?.count ?? 0,
            andel: v?.share ?? 0,
            delvis: b.is_partial ? t('periodChart.yes') : '',
          })
        }
      }
    }
    add(t('periodChart.current'), data.current)
    if (data.comparison) add(t('periodChart.comparison'), data.comparison)
    return rows
  }, [data, t])

  const exportColumns: ColumnDefinition[] = [
    { key: 'periode', label: t('periodChart.period') },
    { key: 'label', label: t(granularity === 'week' ? 'periodChart.week' : 'periodChart.month') },
    { key: 'fra', label: t('periodChart.from') },
    { key: 'til', label: t('periodChart.to') },
    { key: 'merke', label: t('common.brand') },
    { key: 'antall', label: t('common.count') },
    { key: 'andel', label: t('common.marketSharePct'), format: v => Number(v).toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) },
    { key: 'delvis', label: t('periodChart.partialPeriodCol') },
  ]

  const axisColor = isDark ? '#94a3b8' : '#64748b'
  const gridColor = isDark ? '#334155' : '#e2e8f0'

  // A partially covered bucket on the current line gets a hollow marker, so its
  // short value is not read as a drop (#782). A point with no neighbour on its
  // line — a one-week period, say — gets a solid one, since a line needs two
  // points to show at all (#861). Other points get no dot.
  const pointDot = (stroke: string, dataKey: string, markPartial: boolean) =>
    (props: { key?: string; cx?: number; cy?: number; index?: number; value?: number | null; payload?: Record<string, unknown> }) => {
      if (props.value == null || props.cx == null || props.cy == null) return <g key={props.key} />
      const i = props.index ?? 0
      if (markPartial && props.payload?.curPartial) {
        return (
          <circle
            key={props.key}
            cx={props.cx}
            cy={props.cy}
            r={4}
            fill={isDark ? '#1e293b' : '#ffffff'}
            stroke={stroke}
            strokeWidth={2}
            data-testid="brand-period-partial-dot"
          />
        )
      }
      if (chartData[i - 1]?.[dataKey] == null && chartData[i + 1]?.[dataKey] == null) {
        return <circle key={props.key} cx={props.cx} cy={props.cy} r={4} fill={stroke} data-testid="brand-period-point-dot" />
      }
      return <g key={props.key} />
    }

  const renderTooltip = ({ active, payload }: TooltipProps<number, string>) => {
    if (!active || !payload?.length) return null
    const row = payload[0].payload as Record<string, unknown>
    const block = (prefix: 'c' | 'p', label: unknown, partial: unknown, title: string) =>
      label ? (
        <div className="mt-1">
          <div className="font-semibold">
            {title}: {String(label)}
            {partial ? <span className="ml-1 text-amber-600 dark:text-amber-400">{t('periodChart.partialPeriod')}</span> : null}
          </div>
          {brands.map((b, i) => (
            <div key={b} className="flex justify-between gap-4">
              <span style={{ color: BRAND_COLORS[i % BRAND_COLORS.length] }}>{b}</span>
              <span>{row[`${prefix}_${b}`] == null ? '–' : fmtValue(Number(row[`${prefix}_${b}`]), mode)}</span>
            </div>
          ))}
        </div>
      ) : null
    return (
      <div className="rounded-sm border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card p-2 text-xs shadow-sm">
        {block('c', row.curLabel, row.curPartial, t('periodChart.current'))}
        {hasComparison && block('p', row.prevLabel, row.prevPartial, t('periodChart.comparison'))}
      </div>
    )
  }

  return (
    <div className="card p-6 space-y-4" data-testid="brand-period-chart">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <MerkeMultiSelect
              options={brandOptions}
              selected={selected}
              onChange={next => { setTouched(true); setSelected(next) }}
              max={MAX_BRANDS}
            />
            {mineMerker.length > 0 && (
              <button
                type="button"
                onClick={() => { setTouched(true); setSelected(mineMerker.slice(0, MAX_BRANDS)) }}
                disabled={selected.join('|') === mineMerker.slice(0, MAX_BRANDS).join('|')}
                className="btn-secondary text-sm"
                data-testid="mine-merker-shortcut"
              >
                {t('periodChart.mineMerker')}
              </button>
            )}
          </div>
          {mineMerker.length > MAX_BRANDS && selected.join('|') === mineMerker.slice(0, MAX_BRANDS).join('|') && (
            <p className="text-xs text-amber-600 dark:text-amber-400" data-testid="mine-merker-capped">
              {t('periodChart.capped', { max: MAX_BRANDS, n: mineMerker.length })}
            </p>
          )}
        </div>
        <ExportButtons
          data={exportRows}
          columns={exportColumns}
          metadata={{
            title: t('exportTitles.periodChart'),
            dateRange: t('periodChart.exportDateRange', { curStart: startDate, curEnd: endDate, prevStart: prevStartDate, prevEnd: prevEndDate }),
            filters: [
              vehicleFilter === 'personbil' ? t('common.filterPersonbil') :
              vehicleFilter === 'varebil' ? t('common.filterVarebil') : '',
              minePostnummer ? t('common.postnummerFilter', { value: minePostnummer }) : '',
              t('periodChart.groupingFilter', { value: t(granularity === 'week' ? 'periodChart.isoWeek' : 'periodChart.monthLower') }),
            ].filter(f => f),
          }}
          disabled={loading}
          onGetChartImage={() => chartImage.current}
        />
      </div>

      <div className="flex flex-wrap items-center gap-4 text-sm">
        <div className="inline-flex rounded-sm border border-gray-200 dark:border-dark-border overflow-hidden" role="group" aria-label={t('periodChart.grouping')}>
          {(['week', 'month'] as const).map(g => (
            <button
              key={g}
              type="button"
              aria-pressed={granularity === g}
              onClick={() => setGranularity(g)}
              className={`px-3 py-1 ${granularity === g ? 'bg-primary-600 text-white' : 'text-gray-700 dark:text-gray-300'}`}
            >
              {t(g === 'week' ? 'periodChart.week' : 'periodChart.month')}
            </button>
          ))}
        </div>
        <div className="inline-flex rounded-sm border border-gray-200 dark:border-dark-border overflow-hidden" role="group" aria-label={t('periodChart.value')}>
          {(['antall', 'andel'] as const).map(m => (
            <button
              key={m}
              type="button"
              aria-pressed={mode === m}
              onClick={() => setMode(m)}
              className={`px-3 py-1 ${mode === m ? 'bg-primary-600 text-white' : 'text-gray-700 dark:text-gray-300'}`}
            >
              {t(m === 'antall' ? 'common.count' : 'common.marketShare')}
            </button>
          ))}
        </div>
        <label className="inline-flex items-center gap-2 text-gray-700 dark:text-gray-300">
          <input type="checkbox" checked={showComparison} onChange={e => setShowComparison(e.target.checked)} />
          {t('periodChart.showComparison')}
        </label>
      </div>

      {selected.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 py-12 text-center" data-testid="brand-period-empty">
          {t('periodChart.empty')}
        </p>
      ) : error ? (
        <p className="text-sm text-red-600 dark:text-red-400 py-12 text-center">{error}</p>
      ) : loading && !data ? (
        <LoadingSpinner />
      ) : (
        <div ref={chartRef} className={loading ? 'opacity-50' : ''}>
          <ResponsiveContainer width="100%" height={420}>
            <LineChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis dataKey="label" tick={{ fill: axisColor, fontSize: 11 }} interval="preserveStartEnd" minTickGap={16} />
              <YAxis
                tick={{ fill: axisColor, fontSize: 11 }}
                tickFormatter={v => (mode === 'antall' ? Number(v).toLocaleString('nb-NO') : `${v} %`)}
              />
              <Tooltip content={renderTooltip} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {brands.map((b, i) => (
                <Line
                  key={`c_${b}`}
                  type="monotone"
                  dataKey={`c_${b}`}
                  name={b}
                  stroke={BRAND_COLORS[i % BRAND_COLORS.length]}
                  strokeWidth={2}
                  dot={pointDot(BRAND_COLORS[i % BRAND_COLORS.length], `c_${b}`, true)}
                  isAnimationActive={false}
                  connectNulls={false}
                />
              ))}
              {hasComparison && brands.map((b, i) => (
                <Line
                  key={`p_${b}`}
                  type="monotone"
                  dataKey={`p_${b}`}
                  name={t('periodChart.comparisonSeries', { brand: b })}
                  stroke={BRAND_COLORS[i % BRAND_COLORS.length]}
                  strokeWidth={1.5}
                  strokeDasharray="5 5"
                  dot={pointDot(BRAND_COLORS[i % BRAND_COLORS.length], `p_${b}`, false)}
                  isAnimationActive={false}
                  connectNulls={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {data && (
        <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="brand-period-method">
          {t('periodChart.source', { vehicles: vehicleText(vehicleFilter, t), date: fmtDate(data.data_through) })}
          {granularity === 'week' ? ` ${t('periodChart.isoWeeksNote')}` : ''}
          {mode === 'andel' ? ` ${t('periodChart.shareNote')}` : ''}
        </p>
      )}
    </div>
  )
}
